import type { UIMessage } from 'ai';
import { z } from 'zod';
import type {
  AgentQuestionItemSchema,
  FileCreatedSchema,
  TaskSnapshotTaskSchema,
} from '@rossum/fabry-api-client';

export type ChatMessageMetadata = {
  feedback?: boolean;
};

export type ChatUIMessage = UIMessage<ChatMessageMetadata>;

export const chatMessageMetadataSchema = z.object({
  feedback: z.boolean().optional(),
});

// Typed against API client interfaces to prevent drift
export const agentQuestionEventSchema = z.object({
  questions: z.array(
    z.object({
      question: z.string(),
      options: z
        .array(z.object({ value: z.string(), label: z.string() }))
        .optional(),
      multi_select: z.boolean().optional(),
    })
  ),
}) satisfies z.ZodType<{ questions: AgentQuestionItemSchema[] }>;

// mr-fabry asks with Mastra's built-in `ask_user` tool instead of `data-agent-question`: the
// question is the input of a `tool-ask_user` part, and the run stays suspended until it is resumed.
export const askUserInputSchema = z.object({
  question: z.string(),
  options: z
    .array(z.object({ label: z.string(), description: z.string().optional() }))
    .optional(),
  selectionMode: z.enum(['single_select', 'multi_select']).optional(),
});

export type AskUserInput = z.infer<typeof askUserInputSchema>;

// Mastra ends a run a processor aborted (mr-fabry's cost guard, say) with this part and a regular
// `finish` — no `error` chunk.
export const tripwireEventSchema = z.object({ reason: z.string() });

export const taskSnapshotEventSchema = z.object({
  tasks: z.array(
    z.object({
      id: z.string(),
      subject: z.string(),
      status: z.enum(['pending', 'in_progress', 'completed']),
      description: z.string().optional(),
    })
  ),
}) satisfies z.ZodType<{ tasks: TaskSnapshotTaskSchema[] }>;

export const fileCreatedEventSchema = z.object({
  filename: z.string(),
  url: z.string(),
}) satisfies z.ZodType<FileCreatedSchema>;

// The lookup configuration as it arrives on the wire. Declared here rather than imported from
// queue-settings, which is a page feature and cannot be imported from: this is mr-fabry's contract,
// and it is structurally what the editor applies.
const lookupConfigurationSchema = z.object({
  dataset: z.string(),
  queries: z
    .array(z.object({ '//': z.string().optional(), aggregate: z.unknown() }))
    .min(1),
  variables: z.record(z.object({ __formula: z.string() })),
});

export type LookupConfiguration = z.infer<typeof lookupConfigurationSchema>;

// No `satisfies`: mr-fabry's draft surface is absent from the generated client.
export const computedFieldDraftEventSchema = z.object({
  kind: z.literal('lookup'),
  // Absent when the agent runs with no field open.
  field_schema_id: z.string().optional(),
  configuration: lookupConfigurationSchema,
});

// A subagent's own tool calls, streamed by Mastra in `data-tool-agent` run
// snapshots and `data-tool-agent-step` parts. Calls, pending calls, results and
// errors all fit this shape.
const subagentToolCallSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  args: z.record(z.unknown()).optional().catch(undefined),
});

const subagentStepSchema = z.object({
  toolCalls: z.array(subagentToolCallSchema),
  toolResults: z.array(subagentToolCallSchema.extend({ result: z.unknown() })),
  toolErrors: z.array(subagentToolCallSchema).catch([]),
});

export const subagentRunPartSchema = z.object({
  type: z.literal('data-tool-agent'),
  id: z.string(),
  data: subagentStepSchema.extend({
    pendingToolCalls: z.array(subagentToolCallSchema),
    steps: z.array(subagentStepSchema),
  }),
});

export const subagentStepPartSchema = z.object({
  type: z.literal('data-tool-agent-step'),
  data: z.object({ runId: z.string(), step: subagentStepSchema }),
});

export const getChatMetadata = (metadata: unknown): ChatMessageMetadata => {
  const result = chatMessageMetadataSchema.safeParse(metadata);
  if (!result.success) return {};
  return result.data;
};
